"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleCheck as CheckCircle2, Phone, ShieldCheck, Hammer, Layers, Wrench, Paintbrush, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const rebuildDisciplines = [{
  name: "Structural framing & insulation",
  detail: "Load-bearing repair, subflooring, joist reinforcement, and R-value thermal insulation.",
  icon: Hammer
}, {
  name: "Rough MEP & utility rough-in",
  detail: "Licensed electrical, plumbing, and mechanical runs brought up to current regional code.",
  icon: Wrench
}, {
  name: "Drywall, mudding & texturing",
  detail: "Level 4 and Level 5 wall finishing ready for seamless paint and trim integration.",
  icon: Layers
}, {
  name: "Cabinetry, flooring & fine trim",
  detail: "Custom kitchen and bath casework, hardwood and tile setting, casing, and final paint.",
  icon: Paintbrush
}];
const continuityBenefits = ["Zero finger-pointing between separate mitigation and rebuild crews", "One unified Xactimate estimate shared directly with your insurance adjuster", "Drying logs and structural moisture clearances handed straight to carpenters", "Direct oversight by local Mid-Atlantic project managers across MD, DC, and VA"];
export default function Overview() {
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Bar Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 mb-10 text-xs font-mono tracking-wider text-muted-foreground uppercase">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary" />
            <span>TriServe Technical Scope • Direct Crew Execution</span>
          </div>
          <div className="flex items-center gap-2">
            <span>Maryland • Washington DC • Northern Virginia</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start">
          {/* Left Column: Narrative, Capabilities, and Unified Contractor Value */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.35,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 flex flex-col gap-6">
            <div className="space-y-3">
              <span className="text-xs font-bold font-mono uppercase tracking-widest text-primary">
                Turnkey Mitigation &amp; Reconstruction
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
                From structural framing to the final coat of paint.
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                One accountable local team rebuilds your home without subcontractor handoffs, scheduling lag, or conflicting insurance estimates.
              </p>
            </div>

            <p className="text-sm sm:text-base text-foreground/90 leading-relaxed">
              When disaster strikes, water extraction and fire remediation are only the first phase. The harder phase is piecing your home back together safely. Because TriServe manages both mitigation and licensed general contracting under one roof, our crews step seamlessly from demolition and drying right into rough carpentry, electrical, and finish trades. You avoid the month-long pauses that happen when mitigation companies walk away and leave you to find an independent builder.
            </p>

            {/* Disciplines Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {rebuildDisciplines.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.name} data-index={i} className="p-4 rounded-md border border-border bg-card text-card-foreground shadow-xs transition-colors hover:border-primary/40">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <span className="p-1.5 rounded-sm bg-primary/10 text-primary">
                        <IconComponent className="size-4" />
                      </span>
                      <h3 className="text-sm font-semibold text-foreground font-default">
                        {item.name}
                      </h3>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.detail}
                    </p>
                  </div>;
            })}
            </div>

            {/* Single-Contractor Continuity Box */}
            <div className="rounded-lg border border-border bg-muted/60 p-5 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <h3 className="text-sm font-bold text-foreground font-default">
                  The single-contractor difference
                </h3>
              </div>
              <ul className="space-y-2.5">
                {continuityBenefits.map((benefit, i) => <li key={benefit} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>)}
              </ul>
            </div>

            {/* Action Callout */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild variant="default" className="font-semibold text-sm">
                <a href="tel:855-435-6631">
                  <Phone className="size-4" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>
              <Button asChild variant="outline" className="text-sm">
                <Link to="/ContactRequestServicePage">
                  Request an on-site estimate
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Documentary Finished Rebuild Photo & Dispatch Spec Card */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.35,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col gap-4">
            <div className="rounded-lg border border-border overflow-hidden bg-card shadow-sm">
              <div className="relative">
                <AspectRatio ratio={4 / 3}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_living_space.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_living_space.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/reconstruction_living_space.png-wvc-sizes" alt="Completed residential reconstruction featuring open-concept framing, fresh drywall, and warm hardwood flooring in Maryland" loading="lazy" decoding="async" className="size-full object-cover" />
                </AspectRatio>

                {/* Documentary Spec Tag Inset */}
                <div className="absolute top-3 left-3 bg-background/95 border border-border px-2.5 py-1 rounded-sm text-[11px] font-mono tracking-tight text-foreground shadow-xs backdrop-blur-xs flex items-center gap-1.5">
                  <Sparkles className="size-3 text-primary" />
                  <span>Completed Living Space Rebuild • Montgomery County, MD</span>
                </div>
              </div>

              {/* Caption & Performance Metrics */}
              <div className="p-4 border-t border-border space-y-3 bg-card">
                <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                  <span>Scope: Full interior restore</span>
                  <span className="text-primary font-bold">100% In-House</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Post-loss reconstruction completed after severe pipe-burst flooding. TriServe conducted structural drying, subfloor framing, drywall replacement, custom kitchen cabinetry install, and final trim paint with zero third-party trade delays.
                </p>

                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border text-center">
                  <div className="p-2 bg-muted/50 rounded-sm">
                    <span className="block text-[10px] font-mono uppercase text-muted-foreground">Insurance Status</span>
                    <span className="text-xs font-semibold text-foreground">Direct carrier settlement</span>
                  </div>
                  <div className="p-2 bg-muted/50 rounded-sm">
                    <span className="block text-[10px] font-mono uppercase text-muted-foreground">Regional Crews</span>
                    <span className="text-xs font-semibold text-foreground">MD • DC • NOVA Local</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact Micro-Card */}
            <div className="rounded-md border border-border p-4 bg-muted/40 flex items-center justify-between gap-3 text-xs">
              <div className="space-y-0.5">
                <span className="font-semibold text-foreground block">Have active structural damage?</span>
                <span className="text-muted-foreground block">Speak directly with a local project superintendent.</span>
              </div>
              <Button asChild size="sm" variant="default" className="shrink-0 font-bold">
                <a href="tel:855-435-6631">
                  855-435-6631
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}